import {IssueState, IssueStateReason} from '@github-ui/issue-pr-state/constants'
import {useMemo} from 'react'
import {graphql, useSubscription} from 'react-relay'
import type {GraphQLSubscriptionConfig} from 'relay-runtime'

import type {PotentialDuplicateIssueUpdate} from '../hooks/use-duplicate-detection'
import type {PotentialDuplicateIssue} from '../PotentialDuplicates'
import type {potentialDuplicateSubscription} from './__generated__/potentialDuplicateSubscription.graphql'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const subscription = graphql`
  subscription potentialDuplicateSubscription($issueId: ID!) {
    issueUpdated(id: $issueId) {
      issueTitleUpdated {
        id
        title
      }
      issueStateUpdated {
        id
        state
        stateReason
      }
    }
  }
`

/** Type-safe conversion of GraphQL state to local type */
function toIssueState(state: string | null | undefined): PotentialDuplicateIssue['state'] | undefined {
  if (state && state in IssueState) {
    return state as PotentialDuplicateIssue['state']
  }
  if (state != null && process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.warn(`[PotentialDuplicateSubscription] Unexpected issue state: ${state}`)
  }
  return undefined
}

/** Type-safe conversion of GraphQL stateReason to local type */
function toIssueStateReason(
  stateReason: string | null | undefined,
): PotentialDuplicateIssue['stateReason'] | undefined {
  // null/undefined are valid values (issue may not have a state reason)
  if (stateReason == null) {
    return stateReason
  }
  if (stateReason in IssueStateReason) {
    return stateReason as PotentialDuplicateIssue['stateReason']
  }
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.warn(`[PotentialDuplicateSubscription] Unexpected issue stateReason: ${stateReason}`)
  }
  return undefined
}

interface UsePotentialDuplicateSubscriptionParams {
  /**
   * Called when the issue is updated via subscription.
   * IMPORTANT: This callback should be stable (wrapped in useCallback) to avoid
   * unnecessary subscription re-creation on every render.
   */
  onUpdate: (update: PotentialDuplicateIssueUpdate) => void
}

/**
 * Subscribe to updates for a potential duplicate issue. When the issue's title or state changes,
 * the onUpdate callback is called with the new values.
 *
 * This hook should only be called when issueNodeId is defined. Use the
 * PotentialDuplicateSubscription component pattern to conditionally render subscriptions.
 *
 * @param issueNodeId - GraphQL node ID of the issue to subscribe to (required)
 * @param params.onUpdate - Callback for updates. Should be stable (memoized with useCallback)
 */
export const usePotentialDuplicateSubscription = (
  issueNodeId: string,
  {onUpdate}: UsePotentialDuplicateSubscriptionParams,
) => {
  const config = useMemo<GraphQLSubscriptionConfig<potentialDuplicateSubscription>>(
    () => ({
      variables: {issueId: issueNodeId},
      subscription,
      updater: (_store, data) => {
        if (!data?.issueUpdated) return

        const {issueTitleUpdated, issueStateUpdated} = data.issueUpdated
        if (issueTitleUpdated || issueStateUpdated) {
          onUpdate({
            nodeId: issueNodeId,
            title: issueTitleUpdated?.title,
            state: toIssueState(issueStateUpdated?.state),
            stateReason: toIssueStateReason(issueStateUpdated?.stateReason),
          })
        }
      },
    }),
    [issueNodeId, onUpdate],
  )

  useSubscription(config)
}
